import React, { useEffect, useState, useRef } from "react";
import Header from "../../components/Header";
import Leftmenu from "../../components/Leftmenu";
import Rightmenu from "../../components/Rightmenu";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import moment from "moment";
import df from "../../../images/Default_pfp.jpg";

function AdminCommitteeMembers() {
  const { user, token } = useOutletContext();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Form / Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    department: "",
    order_no: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchMembers();
  }, [user]);

  const fetchMembers = () => {
    setLoading(true);
    fetch("/api/admin/committee-members", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((resData) => {
        setLoading(false);
        if (resData.data) {
          setMembers(resData.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching committee members:", err);
        setLoading(false);
      });
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      name: "",
      designation: "",
      department: "",
      order_no: members.length + 1,
    });
    setImageFile(null);
    setPreviewImage(null);
    setFormErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowModal(true);
  };

  const handleOpenEdit = (member) => {
    setIsEditing(true);
    setEditingId(member.id);
    setFormData({
      name: member.name || "",
      designation: member.designation || "",
      department: member.department || "",
      order_no: member.order_no ?? 0,
    });
    setImageFile(null);
    setPreviewImage(member.image || null);
    setFormErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (submitting) return;
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!formData.name.trim()) errors.name = ["Name is required."];
    if (!formData.designation.trim()) errors.designation = ["Designation is required."];

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append("name", formData.name);
    data.append("designation", formData.designation);
    data.append("department", formData.department || "");
    data.append("order_no", formData.order_no);

    if (imageFile) {
      data.append("image", imageFile);
    }

    let url = "/api/admin/committee-member/create";
    if (isEditing) {
      url = "/api/admin/committee-member/update";
      data.append("id", editingId);
    }

    fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: data,
    })
      .then((res) => res.json())
      .then((resData) => {
        setSubmitting(false);
        if (resData.errors) {
          setFormErrors(resData.errors);
        } else if (resData.status || resData.data) {
          setShowModal(false);
          fetchMembers();
        } else if (resData.error) {
          alert(resData.error);
        }
      })
      .catch((err) => {
        console.error("Error saving member:", err);
        setSubmitting(false);
        alert("An error occurred while saving the committee member.");
      });
  };

  const handleDelete = (member) => {
    if (!window.confirm(`Are you sure you want to remove "${member.name}" from committee members?`)) {
      return;
    }

    setDeletingId(member.id);
    fetch("/api/admin/committee-member/delete", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: member.id }),
    })
      .then((res) => res.json())
      .then((resData) => {
        setDeletingId(null);
        if (resData.status) {
          fetchMembers();
        } else if (resData.error) {
          alert(resData.error);
        }
      })
      .catch((err) => {
        console.error("Error deleting member:", err);
        setDeletingId(null);
        alert("An error occurred while deleting the committee member.");
      });
  };

  return (
    <div>
      <Header />
      <Leftmenu />
      <Rightmenu />

      <div className="main-content bg-lightblue theme-dark-bg admin-main-page">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap">
              {/* Header Banner */}
              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4 rounded-xxl overflow-hidden">
                <div className="p-4 bg-danger text-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-white text-danger d-flex align-items-center justify-content-center me-3" style={{ width: 48, height: 48 }}>
                      <i className="feather-award font-md"></i>
                    </div>
                    <div>
                      <h2 className="text-white font-md fw-700 mb-0">Alumni Committee Members Manager</h2>
                      <span className="font-xssss text-white opacity-75">
                        Manage members displayed on the landing and alumni portal leadership section
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleOpenAdd}
                    className="btn btn-light text-danger font-xssss fw-700 px-3 py-2 rounded-xl d-flex align-items-center shadow-sm"
                  >
                    <i className="feather-plus me-1 font-xs"></i> Add Committee Member
                  </button>
                </div>
              </div>

              {/* Navigation Hub */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card shadow-xss border-0 p-3 rounded-xxl bg-white">
                    <div className="d-flex flex-wrap gap-2">
                      <Link to="/admin/dashboard" className="btn btn-sm bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-grid me-1 text-primary"></i> Dashboard
                      </Link>
                      <Link to="/admin/pending-accounts" className="btn btn-sm bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-user-check me-1 text-warning"></i> Pending Accounts
                      </Link>
                      <Link to="/admin/pending-posts" className="btn btn-sm bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-check-square me-1 text-danger"></i> Pending Posts
                      </Link>
                      <Link to="/admin/activity-logs" className="btn btn-sm bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-activity me-1 text-primary"></i> Activity Logs
                      </Link>
                      <Link to="/admin/admin-manager" className="btn btn-sm bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-shield me-1 text-success"></i> Admin Manager
                      </Link>
                      <Link to="/admin/committee-members" className="btn btn-sm bg-current text-white font-xssss fw-600 px-3 py-2 rounded-xl">
                        <i className="feather-award me-1"></i> Committee Members ({members.length})
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Committee Members Table */}
              <div className="card w-100 border-0 bg-white shadow-xss rounded-xxl p-4 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div>
                    <h4 className="font-xs fw-700 text-grey-900 mb-0">Current Committee Members</h4>
                    <span className="font-xssss text-grey-500">Sorted by display order number (lowest first)</span>
                  </div>
                  <span className="badge bg-light text-grey-800 font-xssss fw-600 px-3 py-2 rounded-pill">
                    {members.length} {members.length === 1 ? "Member" : "Members"}
                  </span>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-danger" role="status"></div>
                    <p className="mt-3 text-grey-600 font-xsss mb-0">Loading committee members...</p>
                  </div>
                ) : members.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="feather-award text-grey-300 mb-3" style={{ fontSize: "3.5rem" }}></i>
                    <h5 className="fw-700 text-grey-800 mb-1">No Committee Members Found</h5>
                    <p className="text-muted font-xsss mb-3">Committee details will be updated soon.</p>
                    <button onClick={handleOpenAdd} className="btn bg-current text-white font-xssss fw-600 px-3 py-2 rounded-xl">
                      <i className="feather-plus me-1"></i> Add First Member
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-hover mb-0">
                      <thead className="table-light">
                        <tr className="font-xssss text-uppercase text-grey-600">
                          <th style={{ width: "80px" }}>Order</th>
                          <th style={{ width: "80px" }}>Image</th>
                          <th>Name</th>
                          <th>Designation</th>
                          <th>Department / Institute</th>
                          <th className="text-end" style={{ width: "160px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((member) => (
                          <tr key={member.id}>
                            <td>
                              <span className="badge bg-light text-danger fw-700 font-xssss px-2 py-1 rounded">
                                #{member.order_no ?? 0}
                              </span>
                            </td>
                            <td>
                              <img
                                src={member.image || df}
                                alt={member.name}
                                className="rounded-circle shadow-xs"
                                style={{ width: "45px", height: "45px", objectFit: "cover", border: "2px solid #eee" }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = df;
                                }}
                              />
                            </td>
                            <td>
                              <span className="fw-700 text-grey-900 font-xsss d-block">{member.name}</span>
                            </td>
                            <td>
                              <span className="badge rounded-pill px-3 py-1 fw-600 font-xssss" style={{ backgroundColor: "rgba(228, 32, 38, 0.1)", color: "#E42026" }}>
                                {member.designation}
                              </span>
                            </td>
                            <td>
                              <span className="font-xssss text-grey-600">{member.department || "—"}</span>
                            </td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  onClick={() => handleOpenEdit(member)}
                                  className="btn btn-outline-primary font-xssss fw-600 px-3 py-1 rounded-s"
                                  title="Edit Member"
                                >
                                  <i className="feather-edit-2 me-1"></i> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(member)}
                                  disabled={deletingId === member.id}
                                  className="btn btn-outline-danger font-xssss fw-600 px-3 py-1 rounded-e"
                                  title="Delete Member"
                                >
                                  {deletingId === member.id ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <>
                                      <i className="feather-trash-2 me-1"></i> Delete
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-xxl">
              <div className="modal-header bg-danger text-white rounded-top-xxl p-3">
                <h5 className="modal-title font-xs fw-700 text-white mb-0 d-flex align-items-center">
                  <i className={`feather-${isEditing ? "edit-2" : "plus-circle"} me-2`}></i>
                  {isEditing ? "Edit Committee Member" : "Add Committee Member"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseModal}
                  disabled={submitting}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  {/* Photo Preview & Upload */}
                  <div className="text-center mb-4">
                    <div className="position-relative d-inline-block">
                      <img
                        src={previewImage || df}
                        alt="Member Preview"
                        className="rounded-circle shadow-sm"
                        style={{
                          width: "90px",
                          height: "90px",
                          objectFit: "cover",
                          border: "3px solid #E42026",
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = df;
                        }}
                      />
                      <label
                        htmlFor="committee-member-image"
                        className="btn btn-sm btn-danger rounded-circle position-absolute bottom-0 end-0 p-1 shadow"
                        style={{ width: "32px", height: "32px", cursor: "pointer" }}
                        title="Upload Photo"
                      >
                        <i className="feather-camera text-white font-xssss"></i>
                      </label>
                      <input
                        id="committee-member-image"
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="d-none"
                      />
                    </div>
                    <div className="mt-2 font-xssss text-grey-500">
                      Click camera icon to {previewImage ? "change" : "upload"} profile photo
                    </div>
                    {formErrors.image && (
                      <div className="text-danger font-xssss mt-1">{formErrors.image[0]}</div>
                    )}
                  </div>

                  {/* Name */}
                  <div className="form-group mb-3">
                    <label className="font-xssss fw-600 text-grey-700 mb-1">
                      Member Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control rounded-xl font-xsss ${formErrors.name ? "is-invalid" : ""}`}
                      placeholder="e.g. Dr. Sujit Mukherjee"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback font-xssss">{formErrors.name[0]}</div>
                    )}
                  </div>

                  {/* Designation */}
                  <div className="form-group mb-3">
                    <label className="font-xssss fw-600 text-grey-700 mb-1">
                      Designation / Role in Committee <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control rounded-xl font-xsss ${formErrors.designation ? "is-invalid" : ""}`}
                      placeholder="e.g. Patron / Vice Chancellor, Chairperson, Secretary"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      required
                    />
                    {formErrors.designation && (
                      <div className="invalid-feedback font-xssss">{formErrors.designation[0]}</div>
                    )}
                  </div>

                  {/* Department / Organization */}
                  <div className="form-group mb-3">
                    <label className="font-xssss fw-600 text-grey-700 mb-1">
                      Department / Organization (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-xl font-xsss"
                      placeholder="e.g. Techno India University, Tripura"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    />
                  </div>

                  {/* Order */}
                  <div className="form-group mb-2">
                    <label className="font-xssss fw-600 text-grey-700 mb-1">
                      Display Order Number
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control rounded-xl font-xsss"
                      placeholder="0"
                      value={formData.order_no}
                      onChange={(e) => setFormData({ ...formData, order_no: e.target.value })}
                    />
                    <small className="font-xssss text-grey-500">
                      Members will be displayed on the landing page in ascending order (1, 2, 3...).
                    </small>
                  </div>
                </div>

                <div className="modal-footer bg-light rounded-bottom-xxl p-3">
                  <button
                    type="button"
                    className="btn btn-secondary font-xssss fw-600 px-3 py-2 rounded-xl"
                    onClick={handleCloseModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn bg-current text-white font-xssss fw-600 px-4 py-2 rounded-xl"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : isEditing ? (
                      "Update Member"
                    ) : (
                      "Add Member"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCommitteeMembers;

